import {
  JsonRpcProvider,
  getAddress,
  parseUnits,
  type ContractRunner,
  type Provider,
  type TransactionResponse,
} from "ethers";
import type {
  Address,
  AgentMessagesResult,
  AuthState,
  BestVaultOption,
  ChainConfig,
  DepositParams,
  DeployVaultResult,
  FeeInfo,
  IWalletAdapter,
  PortfolioSummary,
  RefreshResult,
  ResolvedSurfConfig,
  SurfClientOptions,
  SurfEventMap,
  SupportedAsset,
  TokenConfig,
  TransactionResult,
  VaultInfo,
  WalletName,
  WalletState,
  WithdrawParams,
} from "../types";
import { EventEmitter } from "./EventEmitter";
import { HttpClient } from "../services/HttpClient";
import { AuthService } from "../services/AuthService";
import { FactoryService } from "../services/FactoryService";
import { TokenService } from "../services/TokenService";
import { VaultApiService } from "../services/VaultApiService";
import { VaultService } from "../services/VaultService";
import { addApprovalBuffer, parseTokenAmount } from "../utils/amounts";
import { SurfError, SurfErrorCode } from "../utils/errors";
import { SurfClientBuilder } from "./SurfClientBuilder";

export class SurfClient {
  static create(config: import("../types").SurfConfig): SurfClient {
    const builder = new SurfClientBuilder();
    if (config.projectName && (config.appId ?? config.projectId)) {
      builder.setProject(config.projectName, config.appId ?? config.projectId!);
    }

    if (config.environment) {
      builder.setEnvironment(config.environment);
    }
    if (config.appId) {
      builder.setAppId(config.appId);
    }
    if (config.chainId !== undefined) {
      builder.setChain(config.chainId);
    }
    if (config.rpcUrl) {
      builder.setRpcUrl(config.rpcUrl);
    }
    if (config.apiBaseUrl) {
      builder.setApiBaseUrl(config.apiBaseUrl);
    }
    if (config.factoryAddress) {
      builder.setFactoryAddress(config.factoryAddress);
    }
    if (config.autoApprove !== undefined) {
      builder.setAutoApprove(config.autoApprove);
    }
    if (config.logger) {
      builder.setLogLevel(config.logger);
    }

    return builder.build();
  }

  static builder(): SurfClientBuilder {
    return new SurfClientBuilder();
  }

  private readonly events = new EventEmitter<SurfEventMap>();
  private readonly authState: AuthState = {
    token: null,
    address: null,
    authenticated: false,
    user: null,
  };
  private readonly config: ResolvedSurfConfig;
  private readonly registries: SurfClientOptions["registries"];
  private readonly walletAdapters: Map<string, IWalletAdapter>;
  private readonly httpClient: HttpClient;
  private readonly authService: AuthService;
  private readonly vaultApiService: VaultApiService;
  private readonly tokenService = new TokenService();
  private readonly factoryService = new FactoryService();
  private readonly vaultService = new VaultService();
  private readProvider: Provider;
  private activeWalletAdapter: IWalletAdapter | null = null;
  private walletState: WalletState | null = null;

  constructor(options: SurfClientOptions) {
    this.config = options.config;
    this.registries = options.registries;
    this.walletAdapters = options.walletAdapters;
    this.httpClient = new HttpClient({
      baseUrl: this.config.apiBaseUrl,
      projectName: this.config.projectName,
      projectId: this.config.projectId,
      appId: this.config.appId,
    });
    this.authService = new AuthService(this.httpClient);
    this.vaultApiService = new VaultApiService(this.httpClient);
    this.readProvider = new JsonRpcProvider(this.config.rpcUrl);
  }

  on<K extends keyof SurfEventMap>(
    event: K,
    handler: (data: SurfEventMap[K]) => void,
  ): void {
    this.events.on(event, handler);
  }

  off<K extends keyof SurfEventMap>(
    event: K,
    handler: (data: SurfEventMap[K]) => void,
  ): void {
    this.events.off(event, handler);
  }

  /**
   * Validates the configured `appId` against the backend by calling
   * `GET /api/sdk/public/overview` with the `x-app-id` header. A 2xx response means
   * the appId is valid and returns the overview payload. Call this during client
   * setup (appId is required). Throws `SurfError(INVALID_APP_ID)` if the appId is
   * missing or rejected by the backend.
   */
  async verifyApp(): Promise<unknown> {
    const appId = this.config.appId;
    if (!appId) {
      throw new SurfError(SurfErrorCode.MISSING_PROJECT_ID, "appId is required");
    }
    try {
      return await this.httpClient.get("/api/sdk/public/overview", { "x-app-id": appId });
    } catch (error) {
      throw new SurfError(
        SurfErrorCode.INVALID_APP_ID,
        `App ID "${appId}" is not valid`,
        error,
      );
    }
  }

  async connectWallet(walletName: WalletName | string): Promise<WalletState> {
    const adapter = this.walletAdapters.get(walletName);
    if (!adapter) {
      throw new SurfError(
        SurfErrorCode.WALLET_NOT_INSTALLED,
        `Wallet adapter ${walletName} is not registered`,
      );
    }

    // Disconnect any previously active adapter to clear stale listeners
    if (this.activeWalletAdapter && this.activeWalletAdapter !== adapter) {
      await this.activeWalletAdapter.disconnect();
    }

    const walletState = await adapter.connect(this.config.chainId);
    this.activeWalletAdapter = adapter;
    this.walletState = walletState;

    adapter.onAccountsChanged((accounts) => {
      const next = accounts[0];
      if (!next || !this.walletState) {
        return;
      }
      const oldAddress = this.walletState.address;
      const newAddress = getAddress(next) as Address;
      this.walletState = {
        ...this.walletState,
        address: newAddress,
      };
      this.events.emit("wallet:accountChanged", {
        oldAddress,
        newAddress,
      });
    });

    adapter.onChainChanged((chainId) => {
      if (this.walletState) {
        this.walletState = { ...this.walletState, chainId };
      }
      this.events.emit("wallet:chainChanged", { chainId });
    });

    adapter.onDisconnect(() => {
      this.walletState = null;
      this.activeWalletAdapter = null;
      this.events.emit("wallet:disconnected", undefined);
    });

    this.events.emit("wallet:connected", walletState);
    return walletState;
  }

  async disconnectWallet(): Promise<void> {
    await this.activeWalletAdapter?.disconnect();
    this.activeWalletAdapter = null;
    this.walletState = null;
    this.events.emit("wallet:disconnected", undefined);
  }

  getWalletState(): WalletState | null {
    return this.walletState;
  }

  /**
   * Switches the SDK's active chain (and the connected wallet, if any). After this,
   * all reads/writes target `chainId`: `config.chainId`, the read provider, the
   * factory address, and the token list are updated from the chain registry.
   *
   * The chain must be registered for the active environment, else `UNSUPPORTED_CHAIN`.
   * Auth is unaffected (the cookie session is per-wallet, not per-chain). Note: any
   * custom `rpcUrl`/`factoryAddress` set at creation is replaced by the target chain's
   * registry values (a single override can't serve multiple chains).
   */
  async switchChain(chainId: number): Promise<void> {
    const chain = this.registries[this.config.environment][chainId];
    if (!chain) {
      throw new SurfError(
        SurfErrorCode.UNSUPPORTED_CHAIN,
        `Chain ${chainId} is not configured for ${this.config.environment}`,
      );
    }

    // Switch the connected wallet too (if any); read-only clients can switch without one.
    if (this.activeWalletAdapter) {
      await this.activeWalletAdapter.switchChain(chainId);
      if (this.walletState) {
        this.walletState = { ...this.walletState, chainId };
      }
    }

    // Re-point the SDK's active chain at the target chain's registry values.
    this.config.chainId = chainId;
    this.config.rpcUrl = chain.rpcUrl;
    this.config.factoryAddress = chain.factoryAddress;
    this.readProvider = new JsonRpcProvider(chain.rpcUrl);

    this.events.emit("wallet:chainChanged", { chainId });
  }

  registerWalletAdapter(name: string, adapter: IWalletAdapter): void {
    this.walletAdapters.set(name, adapter);
  }

  async authenticate(): Promise<AuthState> {
    const wallet = this.requireWallet();
    const { message } = await this.authService.getNonce(wallet.address);
    let signature: string;
    try {
      signature = await this.activeWalletAdapter!.signMessage(message);
    } catch (error) {
      throw new SurfError(
        SurfErrorCode.SIGNATURE_REJECTED,
        "Failed to sign authentication message",
        error,
      );
    }

    const login = await this.authService.login(
      wallet.address,
      message,
      signature,
    );
    // The session token is delivered as an httpOnly cookie set by the backend
    // (SameSite=None) and is sent automatically on subsequent requests via
    // credentials: "include". The SDK no longer stores or reads it directly.
    this.authState.token = null;
    this.authState.address = wallet.address;
    this.authState.user = login.user ?? this.authState.user;
    this.authState.authenticated = true;
    this.events.emit("auth:authenticated", this.getAuthState());
    return this.getAuthState();
  }

  getAuthState(): AuthState {
    return { ...this.authState };
  }

  async logout(): Promise<void> {
    this.authState.token = null;
    this.authState.address = null;
    this.authState.user = null;
    this.authState.authenticated = false;
    this.events.emit("auth:logout", undefined);
  }

  /**
   * Extends the session using the existing httpOnly auth cookie — no wallet
   * signature required. Requires a currently-valid session cookie; throws
   * `API_ERROR` (401) if the cookie is missing, expired, or already revoked.
   * The backend rotates the cookie; the old token is invalidated immediately.
   */
  async refreshSession(): Promise<RefreshResult> {
    return this.authService.refresh();
  }

  async getVault(walletAddress?: Address): Promise<VaultInfo> {
    return this.vaultApiService.getVault(walletAddress ?? this.requireWallet().address);
  }

  async getSupportedAssets(chainId?: number): Promise<SupportedAsset[]> {
    return this.vaultApiService.getSupportedAssets(chainId);
  }

  async getAgentMessages(
    walletAddress?: Address,
    page = 1,
    limit = 20,
    from?: string,
    to?: string,
  ): Promise<AgentMessagesResult> {
    const address = walletAddress ?? this.requireWallet().address;
    return this.vaultApiService.getAgentMessages(address, page, limit, from, to);
  }

  async deployVault(): Promise<DeployVaultResult> {
    const wallet = this.requireWallet();
    const existing = await this.getVault(wallet.address);

    // Check if vault is already deployed on the current chain
    const deployedOnCurrentChain = existing.assets?.some(
      (a) => a.chainId === this.config.chainId && !!a.vaultAddress,
    );
    if (existing.exists && deployedOnCurrentChain) {
      throw new SurfError(
        SurfErrorCode.VAULT_ALREADY_EXISTS,
        `Vault already deployed on chain ${this.config.chainId} at ${existing.userVaultAddress}`,
      );
    }

    // If vault exists on another chain, reuse existing salt; otherwise prepare a new one
    const isNewVault = !existing.exists || !existing.deploymentSalt;
    let salt: string;

    if (isNewVault) {
      const prepared = await this.vaultApiService.prepare(this.config.chainId);
      salt = prepared.salt;

      const signer = await this.getSignerRunner();
      const predictedVaultAddress = await this.factoryService.computeVaultAddress(
        this.config.factoryAddress,
        wallet.address,
        salt,
        signer,
      );

      const chainPrediction = prepared.predictions.find(
        (prediction) => prediction.chainId === this.config.chainId,
      );

      if (
        chainPrediction &&
        predictedVaultAddress.toLowerCase() !== chainPrediction.predictedVaultAddress.toLowerCase()
      ) {
        throw new SurfError(
          SurfErrorCode.VAULT_DEPLOY_FAILED,
          `Backend predicted vault address does not match factory computation: chain predicted: ${predictedVaultAddress} :: backend predicted: ${chainPrediction?.predictedVaultAddress}`,
        );
      }
    } else {
      salt = existing.deploymentSalt!;
    }

    const signer = await this.getSignerRunner();
    const predictedVaultAddress = await this.factoryService.computeVaultAddress(
      this.config.factoryAddress,
      wallet.address,
      salt,
      signer,
    );

    const deployOverrides = await this.getGasOverrides();
    // Pre-estimate the gas limit via the read provider (see estimateWriteGas) so the wallet
    // doesn't re-estimate against stale state and fail.
    const readFactory = this.factoryService.contract(
      this.config.factoryAddress,
      this.getReadRunner(),
    );
    const deployGasLimit = await this.estimateWriteGas(readFactory, "deployVault", [
      wallet.address,
      salt,
    ]);
    const tx = await this.factoryService.deployVault(
      this.config.factoryAddress,
      wallet.address,
      salt,
      signer,
      deployGasLimit !== undefined
        ? { ...deployOverrides, gasLimit: deployGasLimit }
        : deployOverrides,
    );
    const receipt = await tx.wait();

    if (!receipt) {
      throw new SurfError(
        SurfErrorCode.TRANSACTION_FAILED,
        "Vault deployment transaction did not return a receipt",
      );
    }

    // Only confirm with backend for first-time deployment
    if (isNewVault) {
      await this.vaultApiService.confirm({
        userVaultAddress: predictedVaultAddress,
        homeChainId: this.config.chainId,
        deploymentSalt: salt,
        initialAssets: [],
      });
    }

    const result = {
      vaultAddress: predictedVaultAddress,
      transactionHash: tx.hash,
      salt,
    };

    this.events.emit("vault:deployed", result);

    return result;
  }

  async getOwnerVaults(owner?: Address): Promise<string[]> {
    return this.factoryService.getOwnerVaults(
      this.config.factoryAddress,
      owner ?? this.requireWallet().address,
      this.getReadRunner(),
    );
  }

  async getOwnerVaultCount(owner?: Address): Promise<number> {
    return this.factoryService.getOwnerVaultCount(
      this.config.factoryAddress,
      owner ?? this.requireWallet().address,
      this.getReadRunner(),
    );
  }

  async isVaultFromFactory(vaultAddress: string): Promise<boolean> {
    return this.factoryService.isVaultFromFactory(
      this.config.factoryAddress,
      vaultAddress,
      this.getReadRunner(),
    );
  }

  async deposit(params: DepositParams): Promise<TransactionResult> {
    const token = this.resolveToken(params.asset);
    const vaultAddress = await this.resolveVaultAddress(params.vaultAddress);
    const signer = await this.getSignerRunner();
    const overrides = await this.getGasOverrides();
    let asset = token.address;

    this.events.emit("deposit:started", {
      asset: params.asset,
      amount: params.amount,
    });

    let amountWei = parseTokenAmount(params.amount, token.decimals);
    if (params.wrapEth) {
      const wrapTx = await this.tokenService.wrapNative(
        amountWei,
        this.getChainConfig().wethAddress,
        signer,
        overrides,
      );
      await wrapTx.wait();
      asset = this.getChainConfig().wethAddress;
    }

    if (this.config.autoApprove) {
      const owner = this.requireWallet().address;
      const allowance = await this.tokenService.allowance(asset, owner, vaultAddress, signer);
      if (allowance < amountWei) {
        const approvalTx = await this.tokenService.approve(
          asset,
          vaultAddress,
          addApprovalBuffer(amountWei),
          signer,
          overrides,
        );
        this.events.emit("deposit:approved", {
          asset,
          txHash: approvalTx.hash,
        });
        await approvalTx.wait();
        // The deposit's gas estimation can race the approval: if the estimating node hasn't
        // yet observed the new allowance, the deposit's `transferFrom` reverts during
        // estimateGas (require(false), no data). Wait until the allowance is actually visible
        // before depositing.
        await this.waitForAllowance(asset, owner, vaultAddress, amountWei);
      }
    }

    const hasInitialDeposit = await this.vaultService.assetHasInitialDeposit(
      vaultAddress,
      asset,
      signer,
    );

    // Resolve the Morpho vault for a first-time deposit up front so the gas pre-estimate below
    // can include it.
    const bestVault = hasInitialDeposit
      ? undefined
      : await this.resolveBestVault(token, params.bestVault, vaultAddress);

    // Pre-estimate the gas limit via the read provider so the wallet doesn't re-estimate
    // against stale post-approval state (the original require(false) revert). See
    // estimateWriteGas.
    const readVault = this.vaultService.contract(vaultAddress, this.getReadRunner());
    const gasLimit = await this.estimateWriteGas(
      readVault,
      hasInitialDeposit ? "userDeposit" : "initialDeposit",
      hasInitialDeposit ? [asset, amountWei] : [asset, bestVault, amountWei],
    );
    const txOverrides = gasLimit !== undefined ? { ...overrides, gasLimit } : overrides;

    const tx = hasInitialDeposit
      ? await this.vaultService.userDeposit(vaultAddress, asset, amountWei, signer, txOverrides)
      : await this.vaultService.initialDeposit(
          vaultAddress,
          asset,
          bestVault!,
          amountWei,
          signer,
          txOverrides,
        );

    this.events.emit("deposit:completed", {
      asset,
      amount: params.amount,
      txHash: tx.hash,
    });

    return this.toTransactionResult(tx);
  }

  async getBestVault(assetSymbol: string): Promise<BestVaultOption[]> {
    return this.vaultApiService.getBestVault(assetSymbol);
  }

  async hasInitialDeposit(asset: string, vaultAddress?: string): Promise<boolean> {
    return this.vaultService.assetHasInitialDeposit(
      await this.resolveVaultAddress(vaultAddress),
      asset,
      this.getReadRunner(),
    );
  }

  async withdraw(params: WithdrawParams): Promise<TransactionResult> {
    const vaultAddress = await this.resolveVaultAddress(params.vaultAddress);
    const signer = await this.getSignerRunner();
    const overrides = await this.getGasOverrides();
    const amountString = params.amount ?? "0";
    let amountWei = 0n;

    if (params.amount && params.amount !== "0") {
      const assetDecimals = await this.tokenService.decimals(
        params.asset,
        this.getReadRunner(),
      );
      amountWei = parseUnits(params.amount, assetDecimals);
    }

    this.events.emit("withdraw:started", {
      asset: params.asset,
      amount: amountString,
    });

    // Pre-estimate the gas limit via the read provider so the wallet doesn't re-estimate
    // against stale state (e.g. right after a deposit) and fail with "missing revert data".
    // See estimateWriteGas.
    const readVault = this.vaultService.contract(vaultAddress, this.getReadRunner());
    const gasLimit = await this.estimateWriteGas(readVault, "withdraw", [params.asset, amountWei]);
    const txOverrides = gasLimit !== undefined ? { ...overrides, gasLimit } : overrides;

    const tx = await this.vaultService.withdraw(
      vaultAddress,
      params.asset,
      amountWei,
      signer,
      txOverrides,
    );

    this.events.emit("withdraw:completed", {
      asset: params.asset,
      amount: amountString,
      txHash: tx.hash,
    });

    return this.toTransactionResult(tx);
  }

  async getWithdrawableAmount(asset: string, vaultAddress?: string): Promise<bigint> {
    return this.vaultService.getAssetVaultAssets(
      await this.resolveVaultAddress(vaultAddress),
      asset,
      this.getReadRunner(),
    );
  }

  async getPortfolioSummary(vaultAddress?: string): Promise<PortfolioSummary> {
    return this.vaultService.getPortfolioSummary(
      await this.resolveVaultAddress(vaultAddress),
      this.getReadRunner(),
    );
  }

  async getAssetProfit(asset: string, vaultAddress?: string): Promise<bigint> {
    return this.vaultService.getAssetProfit(
      await this.resolveVaultAddress(vaultAddress),
      asset,
      this.getReadRunner(),
    );
  }

  async getAssetProfitPercentage(asset: string, vaultAddress?: string): Promise<bigint> {
    return this.vaultService.getAssetProfitPercentage(
      await this.resolveVaultAddress(vaultAddress),
      asset,
      this.getReadRunner(),
    );
  }

  async getTokenBalance(token: string, owner?: string): Promise<bigint> {
    const holder = owner ?? this.requireWallet().address;
    return this.tokenService.balanceOf(token, holder, this.getReadRunner());
  }

  async getAllowedAssets(vaultAddress?: string): Promise<string[]> {
    return this.vaultService.getAllowedAssets(
      await this.resolveVaultAddress(vaultAddress),
      this.getReadRunner(),
    );
  }

  async getFeeInfo(vaultAddress?: string): Promise<FeeInfo> {
    return this.vaultService.getFeeInfo(
      await this.resolveVaultAddress(vaultAddress),
      this.getReadRunner(),
    );
  }

  getConfig(): Readonly<ResolvedSurfConfig> {
    return Object.freeze({ ...this.config });
  }

  getSupportedTokens(): TokenConfig[] {
    return [...this.getChainConfig().tokens];
  }

  private getChainConfig(): ChainConfig {
    const chain = this.registries[this.config.environment][this.config.chainId];
    if (!chain) {
      throw new SurfError(
        SurfErrorCode.UNSUPPORTED_CHAIN,
        `Chain ${this.config.chainId} is not configured for ${this.config.environment}`,
      );
    }
    return chain;
  }

  private resolveToken(asset: string): TokenConfig {
    const token = this.getChainConfig().tokens.find(
      (candidate) => candidate.address.toLowerCase() === asset.toLowerCase(),
    );

    if (!token) {
      throw new SurfError(
        SurfErrorCode.INVALID_CONFIG,
        `Asset ${asset} is not registered on chain ${this.config.chainId}`,
      );
    }

    return token;
  }

  private async resolveBestVault(
    token: TokenConfig,
    bestVault: string | undefined,
    vaultAddress: string,
  ): Promise<string> {
    if (bestVault) {
      return bestVault;
    }

    const candidates = await this.vaultApiService.getBestVault(token.symbol);
    const candidate = candidates.find((item) => item.chainId === this.config.chainId);
    if (candidate) {
      return candidate.vaultAddress;
    }

    const onChainCandidates = await this.vaultService.getAssetAvailableVaults(
      vaultAddress,
      token.address,
      this.getReadRunner(),
    );
    if (onChainCandidates[0]) {
      return onChainCandidates[0];
    }

    throw new SurfError(
      SurfErrorCode.NO_BEST_VAULT,
      `No best vault available for ${token.symbol}`,
    );
  }

  private async resolveVaultAddress(vaultAddress?: string): Promise<string> {
    if (vaultAddress) {
      return vaultAddress;
    }

    const activeChain = this.config.chainId;
    const vault = await this.getVault();

    // The vault can be deployed at a different address per chain. Prefer the
    // active chain's explicit `chainAddresses` entry (e.g. Ethereum).
    const chainSpecific = vault.chainAddresses?.find(
      (entry) => entry.chainId === activeChain,
    )?.vaultAddress;
    if (chainSpecific) {
      return chainSpecific;
    }

    // Otherwise the vault lives at the canonical `userVaultAddress` — but only on
    // chains where it is actually deployed (an asset on that chain carries a vault
    // address). On a chain with no vault we must NOT fall back to userVaultAddress:
    // that address has no contract there and would fail with ethers BAD_DATA.
    const deployedOnActiveChain = vault.assets?.some(
      (a) => a.chainId === activeChain && Boolean(a.vaultAddress),
    );
    if (deployedOnActiveChain && vault.userVaultAddress) {
      return vault.userVaultAddress;
    }

    throw new SurfError(
      SurfErrorCode.VAULT_NOT_FOUND,
      `No vault deployed on chain ${activeChain} — deploy a vault on this chain first.`,
    );
  }

  private requireWallet(): WalletState {
    if (!this.walletState || !this.activeWalletAdapter) {
      throw new SurfError(
        SurfErrorCode.WALLET_NOT_CONNECTED,
        "Wallet connection is required for this operation",
      );
    }
    return this.walletState;
  }

  private async getSignerRunner(): Promise<ContractRunner> {
    this.requireWallet();
    return this.activeWalletAdapter!.getSigner();
  }

  private getReadRunner(): ContractRunner {
    return this.readProvider;
  }

  // On Ethereum mainnet (chainId 1), set a low EIP-1559 fee (the "Low" tier) so the
  // transaction costs as little as possible. `type: 2` is set explicitly: without it,
  // wallets like MetaMask ignore dapp-supplied 1559 fees and substitute their own
  // ("market") estimate. Other chains return no overrides (wallet/provider defaults).
  private async getGasOverrides(): Promise<Record<string, unknown>> {
    if (this.config.chainId !== 1) {
      return {};
    }
    const fees = await this.getEthLowFees();
    if (!fees) {
      return {};
    }
    return {
      type: 2,
      maxPriorityFeePerGas: fees.maxPriorityFeePerGas,
      maxFeePerGas: fees.maxFeePerGas,
    };
  }

  // Low Ethereum fees, tuned to match a wallet's "Low" tier so our "Site suggested" values
  // aren't higher than the wallet's own lowest option. The tip is the 10th-percentile
  // priority fee from `eth_feeHistory` over recent blocks (floored so the tx stays
  // includable). maxFeePerGas uses only 1.25x base-fee headroom (not the 2x EIP-1559
  // "survive 6 blocks" default) — this is what MetaMask was displaying as a high "max" for
  // site-suggested txs. Effective cost is still ~baseFee + tip; trade-off is that a sharp
  // base-fee spike may delay mining (the same trade-off the wallet's Low tier makes). Falls
  // back to the latest block's base fee + floor tip if feeHistory is unavailable.
  private async getEthLowFees(): Promise<
    { maxPriorityFeePerGas: bigint; maxFeePerGas: bigint } | null
  > {
    const FLOOR = 100_000_000n; // 0.1 gwei — minimum tip to remain includable
    const rpc = this.readProvider as unknown as {
      send(method: string, params: unknown[]): Promise<{ reward?: string[][]; baseFeePerGas?: string[] }>;
    };
    try {
      // last 20 blocks, 10th-percentile reward per block
      const history = await rpc.send("eth_feeHistory", ["0x14", "latest", [10]]);
      const baseFees = history.baseFeePerGas ?? [];
      if (baseFees.length > 0) {
        const tips = (history.reward ?? [])
          .map((entry) => BigInt(entry?.[0] ?? "0x0"))
          .filter((tip) => tip > 0n)
          .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
        const median = tips.length ? tips[Math.floor(tips.length / 2)] : undefined;
        let priorityFee = median ?? FLOOR;
        if (priorityFee < FLOOR) {
          priorityFee = FLOOR;
        }
        const baseFee = BigInt(baseFees[baseFees.length - 1] ?? "0x0");
        return { maxPriorityFeePerGas: priorityFee, maxFeePerGas: (baseFee * 5n) / 4n + priorityFee };
      }
    } catch {
      // fall through to the base-fee fallback below
    }
    try {
      const block = await this.readProvider.getBlock("latest");
      const baseFee = block?.baseFeePerGas ?? 0n;
      return { maxPriorityFeePerGas: FLOOR, maxFeePerGas: (baseFee * 5n) / 4n + FLOOR };
    } catch {
      return null;
    }
  }

  // After an approval, the new allowance may not be immediately visible to every node. Poll
  // the read provider until it reflects the approval (or a short budget elapses) so a
  // following deposit doesn't gas-estimate against stale state and revert with require(false).
  private async waitForAllowance(
    asset: string,
    owner: string,
    spender: string,
    min: bigint,
  ): Promise<void> {
    for (let attempt = 0; attempt < 8; attempt++) {
      try {
        const allowance = await this.tokenService.allowance(
          asset,
          owner,
          spender,
          this.getReadRunner(),
        );
        if (allowance >= min) {
          return;
        }
      } catch {
        // transient read error — retry
      }
      await new Promise((resolve) => setTimeout(resolve, 1_000));
    }
    // Proceed anyway: if the allowance is genuinely insufficient the deposit surfaces it.
  }

  // Estimate a write's gas limit via the read provider and hand it to the wallet, so the
  // wallet doesn't re-estimate against stale state — the cause of the post-approval
  // require(false)/no-data revert on first deposits. Scoped to Ethereum mainnet, where that
  // race was seen and where the explicit low-fee overrides already apply. A small buffer is
  // added so a slight state shift between estimate and mining can't cause out-of-gas (you
  // still only pay for gas used). A genuine contract revert is re-thrown (retrying won't
  // help); a transient/non-revert failure returns undefined so the wallet estimates itself.
  private async estimateWriteGas(
    contract: { [method: string]: { estimateGas: (...args: unknown[]) => Promise<bigint> } },
    method: string,
    args: unknown[],
  ): Promise<bigint | undefined> {
    if (this.config.chainId !== 1) {
      return undefined;
    }
    try {
      const from = this.requireWallet().address;
      const gas = await contract[method]!.estimateGas(...args, { from });
      return (gas * 12n) / 10n;
    } catch (error) {
      if (this.isContractRevert(error)) {
        throw error;
      }
      return undefined;
    }
  }

  private isContractRevert(error: unknown): boolean {
    const e = error as {
      code?: string;
      data?: unknown;
      shortMessage?: string;
      message?: string;
      info?: { error?: { data?: unknown } };
      error?: { data?: unknown };
    };
    const data = e?.data ?? e?.info?.error?.data ?? e?.error?.data;
    return (
      e?.code === "CALL_EXCEPTION" ||
      (typeof data === "string" && data.startsWith("0x")) ||
      /execution reverted|revert/i.test(String(e?.shortMessage ?? e?.message ?? ""))
    );
  }

  private toTransactionResult(tx: TransactionResponse): TransactionResult {
    return {
      hash: tx.hash,
      wait: () => tx.wait(),
    };
  }
}
